import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { Credentials, LoginData } from 'app/models/user.model';
import { isEmpty } from 'lodash';
import { LOCAL_STORAGE, SESSION_STORAGE } from 'app/tokens/browser.tokens';
import { appConfig } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly httpBackend = inject(HttpBackend);
  private readonly httpWithoutInterceptor = new HttpClient(this.httpBackend);
  private readonly localStorage = inject(LOCAL_STORAGE);
  private readonly sessionStorage = inject(SESSION_STORAGE);

  readonly noAuthEndPoint = ['/api/auth/user/login', '/api/auth/request-reset-password', '/api/auth/reset-password'];

  checkCredentials(): Credentials {
    const credentials =
      this.readStorageValue<Credentials>(this.localStorage, 'credentials') ?? this.readStorageValue<Credentials>(this.sessionStorage, 'credentials');

    return credentials ?? ({} as Credentials);
  }

  getLoginDataFromStorage(): LoginData | undefined {
    const loginData = this.readStorageValue<LoginData>(this.localStorage, 'loginData');

    return !isEmpty(loginData) ? loginData : undefined;
  }

  setCredentialsToStorage(credentials: Credentials, loginData: LoginData): void {
    const stringifiedCredentials = JSON.stringify(credentials);

    if (loginData.rememberMe) {
      this.localStorage?.setItem('loginData', JSON.stringify(loginData));
    }

    this.localStorage?.setItem('credentials', stringifiedCredentials);
  }

  setCredentials(credentials: Credentials): void {
    this.localStorage?.setItem('credentials', JSON.stringify(credentials));
  }

  clearStorage(): void {
    this.localStorage?.removeItem('credentials');
  }

  login(data: LoginData): Observable<Credentials> {
    const userData = { email: data.email, password: data.password };

    return this.http.post<{ data: Credentials }>('/api/auth/user/login', userData).pipe(map(response => response.data));
  }

  refreshToken(refreshToken: string): Observable<Credentials> {
    return this.httpWithoutInterceptor
      .post<Credentials>(`${appConfig.apiHost}/api/auth/refresh-token`, null, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      })
      .pipe(map(response => response));
  }

  requestPasswordReset(data: { email: string; role: string }): Observable<void> {
    return this.http.post<void>('/api/auth/request-reset-password', data);
  }

  resetPassword(data: { hash: string; newPassword: string }): Observable<{ role: string }> {
    return this.http.put<{ data: { role: string }; statusCode: number }>('/api/auth/reset-password', data).pipe(map(response => response.data));
  }

  private readStorageValue<T>(storage: Storage | null, key: string): T | null {
    const value = storage?.getItem(key);
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }
}
